/**
 * Vue d'ensemble plateforme — port de admin.plateforme.tsx, branché sur
 * /api/admin/apercu : KPIs réels, courbe d'inscriptions 30 jours réelle,
 * seuils de succès produit calculés sur toute la plateforme.
 */
import { useEffect, useState } from 'react';
import { Check, TriangleAlert } from 'lucide-react';
import { appelerApi } from '../../reseau.ts';
import { Card, PageTitle, SectionLabel } from '../ui.tsx';

type Apercu = {
  apercu: Record<string, string>;
  push: { total: number };
  seuils: { taux_profils: number; taux_optin: number; rdv_par_participant: number };
  inscriptions_30j: number[];
};

const LEGENDES: [string, string][] = [
  ['organisations', 'Organisations'],
  ['evenements', 'Événements'],
  ['participants', 'Participants'],
  ['profils_completes', 'Profils complétés'],
  ['rendez_vous', 'Rendez-vous'],
  ['connexions', 'Connexions'],
  ['optins_diagnostic', 'Opt-ins diagnostic'],
  ['pistes_nouvelles', 'Pistes nouvelles'],
];

function Courbe({ serie }: { serie: number[] }) {
  const max = Math.max(1, ...serie);
  const points = serie
    .map((v, i) => {
      const x = (i / Math.max(1, serie.length - 1)) * 100;
      const y = 100 - (v / max) * 92;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      className="h-48 w-full"
      role="img"
      aria-label="Inscriptions sur 30 jours"
    >
      <polyline points={points} fill="none" stroke="#ffffff" strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export function Plateforme() {
  const [donnees, setDonnees] = useState<Apercu | null>(null);

  useEffect(() => {
    void appelerApi<Apercu>('/api/admin/apercu').then((r) => {
      if (r.donnees) setDonnees(r.donnees);
    });
  }, []);

  if (!donnees) return <p className="text-fumee">Chargement…</p>;

  const seuils = [
    { label: 'Profils complétés', cible: '> 70 %', valeur: `${donnees.seuils.taux_profils} %`, ok: donnees.seuils.taux_profils > 70 },
    { label: 'Opt-in diagnostic', cible: '> 25 %', valeur: `${donnees.seuils.taux_optin} %`, ok: donnees.seuils.taux_optin > 25 },
    { label: 'Rdv / participant', cible: '≥ 2', valeur: String(donnees.seuils.rdv_par_participant), ok: donnees.seuils.rdv_par_participant >= 2 },
  ];

  return (
    <>
      <PageTitle
        title="Vue d'ensemble plateforme"
        subtitle={`Toutes les organisations, tous les événements — état au ${new Date().toLocaleDateString('fr-BE', { day: 'numeric', month: 'long', year: 'numeric' })}.`}
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {LEGENDES.map(([cle, legende]) => (
          <Card key={cle}>
            <SectionLabel>{legende}</SectionLabel>
            <p className="mt-2 text-[24px] font-light text-blanc">{donnees.apercu[cle] ?? '0'}</p>
          </Card>
        ))}
        <Card>
          <SectionLabel>Abonnés push</SectionLabel>
          <p className="mt-2 text-[24px] font-light text-blanc">{donnees.push.total}</p>
        </Card>
      </div>

      <Card className="mt-6">
        <SectionLabel>Inscriptions — 30 derniers jours</SectionLabel>
        <div className="mt-4">
          <Courbe serie={donnees.inscriptions_30j} />
        </div>
      </Card>

      <h2 className="display-title mb-4 mt-10 text-[24px] text-blanc">Seuils de succès produit</h2>
      <div className="grid gap-4 sm:grid-cols-3">
        {seuils.map((s) => (
          <Card key={s.label}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <SectionLabel>{s.label}</SectionLabel>
                <p className="mt-2 text-[24px] font-light text-blanc">{s.valeur}</p>
                <p className="mt-1 text-[13px] text-fumee">Cible {s.cible}</p>
              </div>
              {s.ok ? (
                <Check size={18} strokeWidth={1.5} className="text-blanc" />
              ) : (
                <TriangleAlert size={18} strokeWidth={1.5} className="text-blanc" />
              )}
            </div>
          </Card>
        ))}
      </div>
    </>
  );
}
